import React from 'react';
import { Card } from '../components/ui/card';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';

const AboutUs = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      {/* Public Header */}
      <header className="py-4 px-6 bg-white shadow-sm">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <img 
            src="https://customer-assets.emergentagent.com/job_pressed-collab/artifacts/sya4rwpg_Black%20Font%20Transp%20Bg.png" 
            alt="Pressed Logo" 
            className="h-8 cursor-pointer"
            onClick={() => navigate('/')}
          />
          <Button 
            onClick={() => navigate('/')}
            className="bg-blue-600 text-white hover:bg-blue-700"
          >
            Home
          </Button>
        </div>
      </header>

      <div className="max-w-4xl mx-auto fade-in py-12 px-4">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold mb-4 text-gray-900">About Pressed</h1>
          <p className="text-xl text-gray-600">Caregiving Coordination Made Simple</p>
        </div>

        {/* Main Content */}
        <div className="space-y-8">
          {/* What Is Pressed */}
          <Card className="p-8">
            <h2 className="text-3xl font-bold mb-6 text-gray-900">What Is Pressed?</h2>
            <div className="space-y-4 text-gray-700 text-lg leading-relaxed">
              <p>
                Pressed is a comprehensive caregiving coordination platform designed to help families manage care for aging or seriously ill loved ones. The app brings together all the moving parts of family caregiving—medical information, financial tracking, daily tasks, legal documents, and family communication—into one secure, organized space.
              </p>
            </div>
          </Card>

          {/* Why Pressed Exists */}
          <Card className="p-8 bg-blue-50">
            <h2 className="text-3xl font-bold mb-6 text-gray-900">Why Pressed Exists</h2>
            <div className="space-y-4 text-gray-700 text-lg leading-relaxed">
              <p>
                Caregiving for an aging or seriously ill adult affects more than 53 million Americans, yet most families face these challenges without proper tools or coordination. Family caregivers struggle with scattered information across multiple platforms, miscommunication among family members, duplicated efforts, and the overwhelming mental load of tracking medications, appointments, insurance claims, and daily care needs.
              </p>
              <p>
                Pressed eliminates the chaos. The platform provides role-based access for family members, professional caregivers, and trusted advisors, ensuring everyone has the information they need while maintaining appropriate privacy boundaries. From medication schedules to financial tracking, from advance directives to daily care logs, Pressed centralizes everything families need to provide coordinated, quality care.
              </p>
            </div>
          </Card>

          {/* The Story Behind Pressed */}
          <Card className="p-8">
            <h2 className="text-3xl font-bold mb-6 text-gray-900">The Story Behind Pressed</h2>
            <div className="space-y-4 text-gray-700 text-lg leading-relaxed">
              <p>
                Pressed was created by Michelle Carter, a third-generation licensed funeral director, certified grief counselor, and crisis communications expert known as The Death Expert. With over two decades of experience in funeral service and end-of-life care across New York State, Michelle has worked with hundreds of families navigating some of life's most difficult transitions.
              </p>
              <p>
                Through her work in funeral homes, from small family operations handling fewer than 50 cases annually to large establishments managing 600+ cases, Michelle witnessed a consistent pattern. Families arrived overwhelmed, exhausted, and unprepared. They struggled with basic questions: Where are the legal documents? What did Mom want? Who talked to the doctor last? What medications is Dad taking? Who's paying for what?
              </p>
              <p>
                These weren't isolated incidents. Michelle saw the same struggles repeated across different families, different circumstances, and different economic backgrounds. Adult children lived in different states. Siblings had conflicting information. Critical medical decisions were delayed because no one could locate advance directives. Financial stress mounted because multiple family members were duplicating expenses without coordination.
              </p>
              <p className="font-semibold text-gray-900">
                The pattern was clear: families needed a central coordination system before crisis struck, not after.
              </p>
            </div>
          </Card>

          {/* Built From Real Experience */}
          <Card className="p-8 bg-purple-50">
            <h2 className="text-3xl font-bold mb-6 text-gray-900">Built From Real Experience</h2>
            <p className="text-gray-700 text-lg mb-4">The platform reflects insights from:</p>
            <ul className="space-y-3 text-gray-700 text-lg">
              <li className="flex items-start gap-3">
                <span className="text-blue-600 font-bold mt-1">•</span>
                <span><strong>Funeral Service Expertise:</strong> Understanding what families need documented, what questions arise during end-of-life transitions, and how to prepare families before crisis moments</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-blue-600 font-bold mt-1">•</span>
                <span><strong>Crisis Communications Background:</strong> Managing complex, sensitive information flow during high-stress situations (Michelle served as Public Information Officer for both Scarsdale Union Free School District and Putnam County District Attorney's Office)</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-blue-600 font-bold mt-1">•</span>
                <span><strong>Grief Counseling Certification:</strong> Supporting families through difficult transitions with compassion and practical tools</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-blue-600 font-bold mt-1">•</span>
                <span><strong>Direct Caregiver Observation:</strong> Witnessing firsthand what works and what fails when families coordinate care for aging or ill adults</span>
              </li>
            </ul>
          </Card>

          {/* Core Features */}
          <Card className="p-8">
            <h2 className="text-3xl font-bold mb-6 text-gray-900">Core Features Designed for Family Caregiving</h2>
            <p className="text-gray-700 text-lg mb-4">Pressed includes comprehensive modules for every aspect of caregiving coordination:</p>
            <ul className="space-y-3 text-gray-700 text-lg">
              <li className="flex items-start gap-3">
                <span className="text-green-600 font-bold mt-1">✓</span>
                <span><strong>Medical Management:</strong> Track medications, appointments, providers, diagnoses, and health insurance</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-green-600 font-bold mt-1">✓</span>
                <span><strong>Financial Coordination:</strong> Monitor expenses, income sources, insurance policies, and financial accounts</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-green-600 font-bold mt-1">✓</span>
                <span><strong>Legal Documents:</strong> Store and access advance directives, powers of attorney, wills, and trusts</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-green-600 font-bold mt-1">✓</span>
                <span><strong>Daily Care Logs:</strong> Record activities of daily living, mood changes, symptoms, and care provider visits</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-green-600 font-bold mt-1">✓</span>
                <span><strong>Task Management:</strong> Assign and track responsibilities among family members and professional caregivers</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-green-600 font-bold mt-1">✓</span>
                <span><strong>Secure Communication:</strong> Keep all caregiving discussions in one place with role-based permissions</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-green-600 font-bold mt-1">✓</span>
                <span><strong>Contact Directory:</strong> Maintain current information for all healthcare providers, family members, legal advisors, and care team members</span>
              </li>
            </ul>
          </Card>

          {/* Who Pressed Serves */}
          <Card className="p-8 bg-green-50">
            <h2 className="text-3xl font-bold mb-6 text-gray-900">Who Pressed Serves</h2>
            <p className="text-gray-700 text-lg mb-4">Pressed is designed for:</p>
            <ul className="space-y-3 text-gray-700 text-lg">
              <li className="flex items-start gap-3">
                <span className="text-blue-600 font-bold mt-1">→</span>
                <span>Family caregivers coordinating care for aging parents or seriously ill adults</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-blue-600 font-bold mt-1">→</span>
                <span>Adult children managing care from a distance</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-blue-600 font-bold mt-1">→</span>
                <span>Sibling groups who need to share responsibilities and information</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-blue-600 font-bold mt-1">→</span>
                <span>Professional care managers coordinating multiple family members and service providers</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-blue-600 font-bold mt-1">→</span>
                <span>Healthcare advocates working with families on care planning</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-blue-600 font-bold mt-1">→</span>
                <span>Anyone preparing for future caregiving responsibilities before crisis strikes</span>
              </li>
            </ul>
          </Card>

          {/* The Pressed Mission */}
          <Card className="p-8">
            <h2 className="text-3xl font-bold mb-6 text-gray-900">The Pressed Mission</h2>
            <div className="space-y-4 text-gray-700 text-lg leading-relaxed">
              <p>
                Caregiving shouldn't mean chaos. Families deserve tools that reduce stress, improve coordination, and ensure their loved ones receive quality care. Pressed exists to transform caregiving from an overwhelming burden into a manageable, coordinated family effort.
              </p>
              <p>
                Built by someone who has supported hundreds of families through end-of-life transitions, Pressed combines professional expertise with practical technology to serve families when they need it most.
              </p>
            </div>
          </Card>

          {/* About Michelle Carter */}
          <Card className="p-8 bg-gray-50">
            <h2 className="text-3xl font-bold mb-6 text-gray-900">About Michelle Carter, The Death Expert</h2>
            <div className="space-y-4 text-gray-700 text-lg leading-relaxed">
              <p>
                Michelle Carter is a third-generation licensed funeral director, certified grief counselor, and nationally recognized end-of-life planning expert. She operates The Death Expert, providing end-of-life planning coaching and educational resources, and Strategic Storyworks, a communications consulting firm focused on schools and healthcare organizations.
              </p>
              <p>
                Her unique combination of funeral service experience, grief counseling certification, and crisis communications expertise positions her as a leading voice in death literacy, family preparedness, and caregiving coordination.
              </p>
              <p className="pt-4">
                <a 
                  href="https://thedeath.expert" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-800 font-semibold underline"
                >
                  Learn more about Michelle's work at The Death Expert →
                </a>
              </p>
            </div>
          </Card>

          {/* Final CTA */}
          <Card className="p-8 bg-gradient-to-br from-blue-100 to-purple-100 text-center">
            <h2 className="text-3xl font-bold mb-4 text-gray-900">Start Coordinating Better Care Today</h2>
            <p className="text-xl text-gray-700 mb-6">
              Transform caregiving from overwhelming chaos into organized, coordinated family care.
            </p>
            <Button 
              onClick={() => navigate('/')}
              size="lg"
              className="bg-blue-600 text-white hover:bg-blue-700 text-lg px-8 py-6"
            >
              Get Started Free
            </Button>
          </Card>
        </div>

        {/* Footer */}
        <div className="text-center py-8 mt-8 border-t border-gray-200">
          <p className="text-gray-600">
            © 2025 Pressed. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
};

export default AboutUs;
